import { useEffect, useRef, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { FileText, Image as ImageIcon, Upload, Trash2, Loader2, Camera, FolderOpen, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PHASE_LABELS = {
  pickup: "Pickup",
  return: "Return",
  other: "Other",
};
const PHASES = ["pickup", "return", "other"];

/**
 * Per-booking documents & photos. Used at pickup/return to record vehicle condition,
 * customer paperwork, accident reports, fuel receipts, etc. Anyone in the booking
 * (customer, vendor, admin) can upload; the uploader, the booking's vendor, or admin
 * can delete. Backed by `/api/bookings/{id}/documents`.
 */
export default function BookingDocumentsPanel({ bookingId, currentUserId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("pickup");
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/documents`);
      setDocs(data.documents || []);
    } catch (e) {
      // Non-fatal: surface the empty list and rely on user retry.
      console.error("booking documents load failed:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (bookingId) load(); /* eslint-disable-next-line */ }, [bookingId]);

  const onPick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
    const isImage = file.type.startsWith("image/");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/bookings/${bookingId}/documents`, {
        path: up.data.path,
        filename: up.data.filename,
        content_type: up.data.content_type,
        label: label.trim() || null,
        phase,
        kind: isImage ? "photo" : "document",
      });
      toast.success(`${isImage ? "Photo" : "Document"} uploaded`);
      setLabel("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/bookings/${bookingId}/documents/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const byPhase = (p) => docs.filter((d) => (d.phase || "other") === p);

  return (
    <div className="rounded-3xl border border-border bg-white p-6" data-testid="booking-documents-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-medium flex items-center gap-2">
            <Camera className="h-5 w-5 text-blue-600" />
            Photos & documents
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">Attach vehicle photos and paperwork. Use pickup and return phases to keep handover evidence organised. Shared with the customer, vendor and admin.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-dashed border-border bg-neutral-50/50 p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-[160px,1fr,auto] gap-2 items-end">
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Phase</label>
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger className="rounded-xl bg-white" data-testid="booking-doc-phase"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PHASES.map((p) => <SelectItem key={p} value={p}>{PHASE_LABELS[p]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Optional label</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Front bumper · Damage to driver door · Fuel receipt" className="rounded-xl bg-white" data-testid="booking-doc-label" />
          </div>
          <Button onClick={onPick} disabled={uploading} className="rounded-full bg-blue-600 hover:bg-blue-700 h-10" data-testid="booking-doc-upload">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading ? "Uploading…" : "Add photo or PDF"}
          </Button>
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" className="hidden" onChange={onFile} data-testid="booking-doc-input" />
        <div className="text-[10px] text-neutral-500 mt-2">JPG, PNG, WebP or PDF · max 8 MB per file.</div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading attachments…</div>
      ) : docs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-neutral-500" data-testid="booking-docs-empty">
          No attachments yet. Add photos at pickup and again at return for a complete record.
        </div>
      ) : (
        <div className="space-y-5" data-testid="booking-docs-list">
          {PHASES.map((p) => {
            const list = byPhase(p);
            if (list.length === 0) return null;
            return (
              <div key={p}>
                <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2 flex items-center gap-1.5">
                  <FolderOpen className="h-3 w-3" />
                  {PHASE_LABELS[p]} · {list.length}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {list.map((d) => (
                    <DocumentTile key={d.id} doc={d} canDelete={d.uploaded_by === currentUserId} onDelete={() => remove(d.id)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DocumentTile({ doc, canDelete, onDelete }) {
  const url = fileUrl(doc.path);
  const isImage = (doc.content_type || "").startsWith("image/") || /\.(jpg|jpeg|png|webp|gif)$/i.test(doc.path || "");
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-white flex flex-col" data-testid={`booking-doc-${doc.id}`}>
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        {isImage ? (
          <img src={url} alt={doc.label || doc.filename} className="w-full h-28 object-cover" />
        ) : (
          <div className="h-28 bg-neutral-50 flex flex-col items-center justify-center gap-1">
            <FileText className="h-7 w-7 text-rose-600" />
            <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-semibold">PDF</span>
          </div>
        )}
      </a>
      <div className="px-2.5 py-2 flex items-start justify-between gap-1 flex-1">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium truncate">{doc.label || doc.filename || (isImage ? "Photo" : "Document")}</div>
          <div className="text-[9px] text-neutral-500 flex items-center gap-1">
            {isImage ? <ImageIcon className="h-2.5 w-2.5" /> : <FileText className="h-2.5 w-2.5" />}
            {doc.uploaded_by_role || "user"}
          </div>
        </div>
        <div className="flex flex-col gap-0.5 shrink-0">
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700" title="Open" data-testid={`booking-doc-open-${doc.id}`}>
            <ExternalLink className="h-3 w-3" />
          </a>
          {canDelete && (
            <button onClick={onDelete} type="button" className="text-rose-600 hover:text-rose-700" title="Delete" data-testid={`booking-doc-delete-${doc.id}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
